<script setup lang="ts">
import { z } from 'zod';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { UiForm, UiFormField, UiInputField, UiTextarea, UiSelect, UiText, UrgencyFormOptions } from '@shopexperts/shared';
import type { PublicExpertMinimal, PackagedServiceMinimal, Urgency } from '@shopexperts/shared';
import LeadStepHeader from '../leads/LeadStepHeader.vue';
import LeadStepActions from '../leads/LeadStepActions.vue';
import PreferredExpertCard from '../leads/PreferredExpertCard.vue';
import {
  formatProjectValueRangeId,
  parseProjectValueRangeId,
  PROJECT_VALUE_RANGE_SELECT_OPTIONS,
} from '@/app/constants/projectValueRanges';

interface Props {
  currentStep: number;
  totalSteps: number;
  expert?: PublicExpertMinimal;
  packagedService?: PackagedServiceMinimal;
  isLoadingExpert?: boolean;
  hasDraftToken?: boolean;
  caption?: string;
  heading?: string;
  subheading?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  showLoginHint?: boolean;
  showAttachmentNote?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  expert: undefined,
  packagedService: undefined,
  isLoadingExpert: false,
  hasDraftToken: false,
  caption: 'Project Details',
  heading: undefined,
  subheading: undefined,
  emailLabel: 'Your Email Address',
  emailPlaceholder: 'your@email.com',
  showLoginHint: true,
  showAttachmentNote: true,
});

const headingText = computed(() => {
  if (props.heading) return props.heading;
  if (props.expert?.name) return `Tell ${props.expert.name} about your project`;
  return 'Tell us about your project';
});

const subheadingText = computed(() => {
  if (props.subheading) return props.subheading;
  if (props.expert?.name) {
    return `You've chosen ${props.expert.name} as your preferred expert. Share your project details so they can review your request.`;
  }
  return 'Provide the key details about your project so we can match you with the right Shopify expert.';
});

const emit = defineEmits<{
  (e: 'next', data: ProjectDetailsData): void;
  (e: 'emailBlur', email: string): void;
  (e: 'fieldBlur', data: Partial<ProjectDetailsData>): void;
}>();

// Track last emitted email to avoid duplicate calls
const lastEmittedEmail = ref<string>('');

export interface ProjectDetailsData {
  email: string;
  projectTitle: string;
  projectDescription?: string;
  urgency?: Urgency;
  projectValueRangeId?: number;
}

interface ProjectDetailsFormModel {
  email: string;
  projectTitle: string;
  projectDescription: string;
  urgency: string;
  projectValueRangeId: string;
}

const schema = z.object({
  email: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address')
      .max(255, 'Email must be 255 characters or less')
  ),
  projectTitle: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
      .min(1, 'Project title is required')
      .max(255, 'Project title must be 255 characters or less')
  ),
  projectDescription: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string().min(1, 'Project description is required')
  ),
  urgency: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string().min(1, 'Project timeline is required')
  ),
  projectValueRangeId: z.preprocess(
    (val) => val === undefined || val === null ? '' : val,
    z.string()
  ),
});

const model = defineModel<ProjectDetailsData>({
  default: () => ({
    email: '',
    projectTitle: '',
    projectDescription: '',
    urgency: undefined,
    projectValueRangeId: undefined,
  }),
});

const formModel = computed<ProjectDetailsFormModel>({
  get: () => ({
    email: model.value.email ?? '',
    projectTitle: model.value.projectTitle ?? '',
    projectDescription: model.value.projectDescription ?? '',
    urgency: model.value.urgency ?? '',
    projectValueRangeId: formatProjectValueRangeId(model.value.projectValueRangeId),
  }),
  set: (value) => {
    model.value = {
      email: value.email,
      projectTitle: value.projectTitle,
      projectDescription: value.projectDescription,
      urgency: value.urgency as Urgency,
      projectValueRangeId: parseProjectValueRangeId(value.projectValueRangeId),
    };
  },
});

// Email validation schema for blur check
const emailSchema = z.string().min(1).email();

function handleEmailBlur() {
  const email = model.value.email;
  
  // Only emit if email is valid and different from last emitted
  const isValid = emailSchema.safeParse(email).success;
  if (isValid && email !== lastEmittedEmail.value) {
    lastEmittedEmail.value = email;
    emit('emailBlur', email);
  }
}

function handleFieldBlur(fieldName: keyof ProjectDetailsData) {
  // Only emit field blur if we have a draft token (draft was created)
  if (props.hasDraftToken) {
    emit('fieldBlur', { [fieldName]: model.value[fieldName] });
  }
}

function onSubmit(values: { email: string; projectTitle: string; projectDescription: string; urgency: string; projectValueRangeId: string }) {
  emit('next', {
    email: values.email,
    projectTitle: values.projectTitle,
    projectDescription: values.projectDescription,
    urgency: values.urgency as Urgency,
    projectValueRangeId: parseProjectValueRangeId(values.projectValueRangeId),
  });
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <LeadStepHeader
      :caption="caption"
      :heading="headingText"
      :subheading="subheadingText"
      :current-step="currentStep"
      :total-steps="totalSteps"
    >
      <template #close-button>
        <slot name="close-button" />
      </template>
      <template #after-subheading>
        <UiText v-if="showLoginHint" as="p" variant="p" class="text-se-gray-600">
          Already have an account? <RouterLink to="/login" class="text-se-primary-600 underline hover:text-se-primary-700">Login here.</RouterLink>
        </UiText>
      </template>
    </LeadStepHeader>

    <!-- Preferred expert card (step 1 only, directly under the header) -->
    <PreferredExpertCard
      v-if="props.expert"
      :expert="props.expert"
      :packaged-service="props.packagedService"
    />
    <div
      v-else-if="props.isLoadingExpert"
      class="animate-pulse flex items-center gap-3 p-3 bg-se-gray-50 rounded-lg border border-se-gray-200"
    >
      <div class="w-10 h-10 bg-se-gray-200 rounded-full" />
      <div class="flex flex-col gap-2">
        <div class="w-32 h-4 bg-se-gray-200 rounded" />
        <div class="w-20 h-3 bg-se-gray-200 rounded" />
      </div>
    </div>

    <UiForm
      v-model="formModel"
      :schema="schema"
      @submit="onSubmit"
    >
      <div class="flex flex-col gap-5">
        <!-- Email -->
        <UiFormField
          name="email"
          :label="emailLabel"
          type="email"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiInputField
              :id="id"
              name="email"
              type="email"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              @validate="() => { onValidate(); handleEmailBlur(); }"
              :invalid="invalid"
              :error="error"
              :placeholder="emailPlaceholder"
            />
          </template>
        </UiFormField>

        <!-- Project Title -->
        <UiFormField
          name="projectTitle"
          label="Project Title"
          type="text"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiInputField
              :id="id"
              name="projectTitle"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              @validate="() => { onValidate(); handleFieldBlur('projectTitle'); }"
              :invalid="invalid"
              :error="error"
              placeholder="eg. Redesign my Shopify Product Page"
            />
          </template>
        </UiFormField>

        <!-- Project Description -->
        <UiFormField
          name="projectDescription"
          label="Project Description"
          type="textarea"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiTextarea
              :id="id"
              name="projectDescription"
              :model-value="modelValue as string"
              @update:model-value="onUpdateModelValue"
              @validate="() => { onValidate(); handleFieldBlur('projectDescription'); }"
              :invalid="invalid"
              :error="error"
              placeholder="Describe your project goals, requirements, and any specific details that would help experts understand your needs..."
              :rows="5"
            />
          </template>
        </UiFormField>

        <div
          v-if="showAttachmentNote"
          class="rounded-lg bg-se-gray-100 border border-se-gray-200 p-4"
        >
          <UiText as="p" variant="p-small" text="*Note:* You can share files, documents, and additional materials with your matched expert after submitting your request." />
        </div>

        <!-- Urgency Select -->
        <UiFormField
          name="urgency"
          label="Project Timeline"
          type="select"
        >
          <template #default="{ modelValue, onUpdateModelValue, id, invalid, error }">
            <UiSelect
              :id="id"
              name="urgency"
              :model-value="modelValue as string"
              @update:model-value="(value) => { onUpdateModelValue(value); handleFieldBlur('urgency'); }"
              :options="UrgencyFormOptions"
              placeholder="When do you need this completed?"
              :invalid="invalid"
              :error="error"
            />
          </template>
        </UiFormField>

        <UiFormField
          name="projectValueRangeId"
          label="Budget (optional)"
          type="select"
        >
          <template #default="{ modelValue, onUpdateModelValue, id, invalid, error }">
            <UiSelect
              :id="id"
              name="projectValueRangeId"
              :model-value="modelValue as string"
              @update:model-value="(value) => { onUpdateModelValue(value); handleFieldBlur('projectValueRangeId'); }"
              :options="PROJECT_VALUE_RANGE_SELECT_OPTIONS"
              placeholder="Select a budget range"
              :invalid="invalid"
              :error="error"
            />
          </template>
        </UiFormField>

        <LeadStepActions
          :current-step="currentStep"
          :total-steps="totalSteps"
          next-label="Continue"
        />
      </div>
    </UiForm>
  </div>
</template>
